import {
  CopyValue,
  ITile,
  MyVaroSectionTile,
  MyVaroVxlTile,
  ProductOfferCardTile,
  Query,
  RouteNavigationAction,
  Tag,
  TileStatus,
  Typenames,
  UserAvatar,
  VxlListItem,
  VxlListItemTrailingAccessoryResult,
  VxlSectionDivider,
} from '@varo/graphql-schema-types';
import { Href, ImperativeRouter, useRouter } from 'expo-router';
import { getMyVaroListItemAnalytics } from 'features/myVaro/helpers/MyVaroAnalytics.helpers';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import FetchUser from 'services/graphql/queries/FetchUser';
import MyVaroPageTile from 'services/graphql/queries/MyVaroPageTile';
import {
  Forest,
  Size,
} from 'shared/components/vxl/avatar/UserAvatar/UserAvatar.helpers';
import {
  SectionedContentScreenModel,
  SectionedContentScreenSection,
} from 'shared/containers/vxl/SectionedContentScreen/SectionedContentScreen';
import {
  PressableAnalyticsProps,
  usePressableAnalytics,
} from 'shared/hooks/analytics/usePressableAnalytics';
import { useDefaultErrorStrings } from 'shared/hooks/errorFormatters/useDefaultError/useDefaultErrorStrings';
import { useFormattedCombinedError } from 'shared/hooks/errorFormatters/useFormattedCombinedError/useFormattedCombinedError';
import {
  ErrorScreenProps,
  ErrorScreenType,
} from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';
import { AuthContext, AuthContextProps } from 'shared/providers/auth';
import {
  EnvironmentContext,
  EnvironmentContextProps,
} from 'shared/providers/environment';
import { Environment } from 'shared/providers/environment.constants';
import { VXLComponentId, VXLGroupId } from 'shared/renderer/types';
import theme from 'shared/styles/theme';
import { getShortExpoUpdateId } from 'shared/utils/appUpdateInfo/appUpdateInfo';
import { buildNumber, displayVersion } from 'shared/utils/appVersionUtils';
import { tagColorMapper } from 'shared/utils/tagColorMapper/tagColorMapper';
import { Maybe } from 'shared/utils/types';
import { type AnyVariables, useQuery } from 'urql';

export interface MyVaroResponse {
  title?: string;
  sectionList: SectionedContentScreenModel;
}

export type LoadingState = null | 'refreshing' | 'loading';

interface MyVaroScreenState {
  loadingState: LoadingState;
  response?: MyVaroResponse;
  errorModel?: Maybe<ErrorScreenProps>;
  refresh: (useInlineLoading: boolean) => void;
}

const formatMyVaroData = (
  userData: Query,
  myVaroData: Query,
  router: ImperativeRouter,
  handleLogout: () => Promise<void>,
  currentEnv: Environment,
): MyVaroResponse | undefined => {
  if (!userData || !myVaroData) {
    return undefined;
  }

  const myVaroTilesData = myVaroData.myVaroPageTile;
  const isUat = currentEnv === Environment.UAT;

  const handleTileOnPress = (action: Maybe<RouteNavigationAction>) => {
    if (action?.route) {
      router.push(action.route as Href);
    }
  };

  const handleSandboxTileOnPress = () => {
    router.push('/myvaro/sandbox');
  };

  let sectionIndex = 0;
  const myVaroListTile = (tile: ITile) => {
    if (
      '__typename' in tile &&
      tile.__typename === Typenames.MyVaroSectionTile
    ) {
      const vxlTile = tile as MyVaroSectionTile;

      const myVaroTileData = vxlTile.tiles.map(
        (tile: MyVaroVxlTile | ProductOfferCardTile, tileIndex: number) => {
          if (
            '__typename' in tile &&
            tile.__typename === Typenames.MyVaroVxlTile &&
            tile.status === TileStatus.Loaded
          ) {
            const vxlListItemContent = tile.content as VxlListItem;

            const vxlListItemTrailingAccessory =
              vxlListItemContent.trailingAccessory as VxlListItemTrailingAccessoryResult;

            let trailingAccessory = {};

            if (vxlListItemTrailingAccessory?.__typename === Typenames.Badge) {
              trailingAccessory = {
                id: vxlListItemTrailingAccessory.text,
                name: VXLComponentId.BADGE,
                props: {
                  badgeValue: Number(vxlListItemTrailingAccessory.text),
                },
              };
            } else if (
              vxlListItemTrailingAccessory?.__typename === Typenames.Tag
            ) {
              trailingAccessory = {
                id: vxlListItemTrailingAccessory.text,
                name: VXLComponentId.TAG,
                props: {
                  type: tagColorMapper(
                    (vxlListItemTrailingAccessory as Tag)?.tagColor,
                  ),
                  text: vxlListItemTrailingAccessory.text,
                },
              };
            }
            const analyticsId = vxlListItemContent?.primaryText
              .toLowerCase()
              .split(' ')
              .join('-');

            return {
              id: tile.id,
              name: VXLComponentId.LIST_ITEM,
              props: {
                analyticsId: analyticsId,
                componentAnalytics: getMyVaroListItemAnalytics(
                  tile.id,
                  tileIndex,
                ),
                testID: tile.id,
                primaryCopy: vxlListItemContent.primaryText,
                secondaryCopy: vxlListItemContent.secondaryText,
                showsChevron: vxlListItemContent.showsChevron,
                onPress: () => {
                  handleTileOnPress(
                    vxlListItemContent?.onPressAction as RouteNavigationAction,
                  );
                },
                trailingAccessory,
              },
            };
          }
          // Return an empty list item if the tile type is not recognized
          return {
            id: tile.id,
            name: VXLComponentId.LIST_ITEM,
            props: {
              primaryCopy: '',
              secondaryCopy: '',
              showsChevron: false,
            },
          };
        },
      );

      const myVaroTileDataWithSandbox = [
        ...myVaroTileData,
        ...(isUat
          ? [
              {
                id: '"MY_VARO_PROFILE_SECTION_SANDBOX_LIST_ITEM"',
                name: VXLComponentId.LIST_ITEM,
                props: {
                  analyticsId: 'sandbox',
                  primaryCopy: 'Sandbox',
                  secondaryCopy: 'Internal testing area',
                  showsChevron: true,
                  onPress: () => {
                    handleSandboxTileOnPress();
                  },
                },
              },
            ]
          : []),
      ];

      const content = (vxlTile?.header as MyVaroVxlTile).content as VxlListItem;
      const userInitials = (
        (content?.leadingAccessory as UserAvatar)?.userAvatarAsset as CopyValue
      )?.text;
      const userPhotoUrl = userData.user?.customerInfo?.profilePhoto?.url;
      const userAvatarVariant = userPhotoUrl
        ? { url: userPhotoUrl }
        : {
            initials: userInitials,
          };

      const mapped = {
        sectionHeader: content.primaryText
          ? {
              id: vxlTile.id,
              name: VXLComponentId.LIST_ITEM,
              props: {
                primaryCopy: content.primaryText ?? '',
                secondaryCopy: content.secondaryText ?? '',
                showsChevron: false,
                leadingAccessory: content.leadingAccessory
                  ? {
                      id: 'USER_INFO_LIST_ITEM_AVATAR',
                      name: VXLComponentId.USER_AVATAR,
                      props: {
                        userAvatarVariant: userAvatarVariant,
                        userAvatarTheme: Forest,
                        userAvatarSize: Size.Medium,
                        userAvatarShowVaroLogo: false,
                      },
                    }
                  : undefined,
              },
            }
          : {
              id: vxlTile.id,
              name: VXLComponentId.SECTION_DIVIDER,
              props: {
                labelText:
                  (content as unknown as VxlSectionDivider).labelText ?? '',
              },
            },
        sectionFooter: undefined,
        data: sectionIndex === 0 ? myVaroTileDataWithSandbox : myVaroTileData,
      };
      sectionIndex += 1;
      return mapped;
    }

    return {
      sectionHeader: undefined,
      sectionFooter: undefined,
      data: [],
    };
  };

  const additionalSections: SectionedContentScreenSection[] = myVaroTilesData
    ? myVaroTilesData.tiles.map(myVaroListTile)
    : [];

  const shortOtaUpdateId = getShortExpoUpdateId();

  const structuredSectionData = {
    sectionList: {
      sections: [
        {
          sectionHeader: undefined,
          sectionFooter: undefined,
          data: [],
        },
        ...additionalSections,
      ],
      footer: {
        id: '98',
        name: VXLGroupId.COMPONENT_GROUP,
        props: {
          alignItems: 'center',
          padding: theme.spacing.m,
          gap: theme.spacing.m,
          backgroundColor: theme.color.background.tertiary,
          components: [
            {
              id: '4',
              name: VXLComponentId.BUTTON,
              props: {
                label: 'Log out',
                width: 'FULL',
                analyticsId: 'myVaroLogOutClicked',
                onPress: handleLogout,
              },
            },
            {
              id: '5',
              name: VXLComponentId.BODY_XS_TEXT,
              props: {
                children: `Version ${displayVersion} ${buildNumber ? `(${buildNumber})` : ''}`,
              },
            },
            ...(shortOtaUpdateId
              ? [
                  {
                    id: '6',
                    name: VXLComponentId.BODY_XS_TEXT,
                    props: {
                      children: `Update ${shortOtaUpdateId}`,
                      style: { marginTop: -theme.spacing.s },
                    },
                  },
                ]
              : []),
          ],
        },
      },
    },
  };

  return structuredSectionData;
};

const useMyVaroData = (): MyVaroScreenState => {
  const router = useRouter();
  const refreshingRef = useRef(false);
  const { handleLogout } = useContext<AuthContextProps>(AuthContext);
  const [analyticsIdString] = useState<PressableAnalyticsProps | undefined>(
    undefined,
  );

  const { currentEnv } =
    useContext<EnvironmentContextProps>(EnvironmentContext);

  const trackAnalyticsEvent = usePressableAnalytics({
    analyticsId: analyticsIdString?.analyticsId,
    customAttributes: analyticsIdString?.customAttributes,
  });

  useEffect(() => {
    if (analyticsIdString && trackAnalyticsEvent) {
      trackAnalyticsEvent();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [analyticsIdString]);

  const [
    { data: userData, fetching: userFetching, error: userError },
    refetchUserQuery,
  ] = useQuery<Query, AnyVariables>({
    query: FetchUser,
    requestPolicy: 'cache-and-network',
  });

  const [
    {
      data: myVaroPageData,
      fetching: myVaroPageFetching,
      error: myVaroPageError,
    },
    refetchMyVaroPagQuery,
  ] = useQuery<Query, AnyVariables>({
    query: MyVaroPageTile,
    requestPolicy: 'cache-and-network',
  });

  const isRefreshing = refreshingRef.current;
  const isFetching = userFetching || myVaroPageFetching;

  const loadingState = useMemo(() => {
    if (isRefreshing && isFetching) {
      return 'refreshing';
    } else if (isFetching) {
      return 'loading';
    }
    return null;
  }, [isRefreshing, isFetching]);

  const refresh = (useInlineLoading: boolean): void => {
    refreshingRef.current = useInlineLoading;
    refetchUserQuery({ requestPolicy: 'network-only' });
    refetchMyVaroPagQuery({ requestPolicy: 'network-only' });
  };

  const response = formatMyVaroData(
    userData as Query,
    myVaroPageData as Query,
    router,
    handleLogout,
    currentEnv,
  );

  const errors = userError || myVaroPageError;
  const responseError = useFormattedCombinedError(errors);
  const { defaultTitle, defaultMessage, retryButtonTitle, cancelButtonTitle } =
    useDefaultErrorStrings();
  const buttons = {
    primaryButton: {
      title: retryButtonTitle,
      onPress: () => refresh(isRefreshing),
    },
    secondaryButton: {
      title: cancelButtonTitle,
      onPress: () => router.back(),
    },
  };

  const errorModel = errors
    ? {
        screenType: ErrorScreenType.Error,
        messageHeadline: responseError?.title ?? defaultTitle,
        messageBody: responseError?.message ?? defaultMessage,
        ...buttons,
      }
    : undefined;

  return {
    loadingState,
    response,
    refresh,
    errorModel,
  };
};

export default useMyVaroData;
